import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";
import { LangSwitcher } from "@/components/LangSwitcher";
import { LegalLinks } from "@/components/LegalLinks";
import { HexField } from "@/components/HexField";
import { Wordmark } from "@/components/Nav";

export const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
    <path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.7-4.1-13.6-9.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

export const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export function AuthShell({ title, subtitle, children, footer }) {
  const { config } = useAuth();
  return (
    <div className="grain relative flex min-h-screen flex-col overflow-hidden" data-testid="auth-page">
      <HexField className="pointer-events-none absolute right-0 top-0 hidden w-[52vw] max-w-[1000px] opacity-[.35] lg:block" />
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <Wordmark brand={config.brand} />
        <LangSwitcher compact />
      </header>
      <main className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 items-center px-6 pb-16">
        <div className="grid w-full gap-12 md:grid-cols-[1fr_440px] md:items-center">
          <div className="reveal relative hidden md:block">
            <div className="relative">
              <span className="mono-label">02</span>
              <h1 className="mt-6 max-w-lg text-4xl font-semibold leading-tight tracking-tight text-vox-text lg:text-6xl">{title}</h1>
              {subtitle && <p className="mt-6 max-w-md text-base leading-relaxed text-vox-muted md:text-lg" data-testid="auth-subtitle">{subtitle}</p>}
            </div>
          </div>
          <div className="panel reveal reveal-1 p-7 md:p-8">
            <h2 className="mb-6 text-lg font-semibold text-vox-text md:hidden">{title}</h2>
            {children}
            {footer && <div className="mt-6 text-center text-sm text-vox-dim">{footer}</div>}
            <div className="mt-4 text-center"><LegalLinks /></div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      login(data);
      navigate("/dashboard");
    } catch (err) {
      const code = errCode(err);
      setError(t(`errors.${code}`, code));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("auth.loginTitle")} subtitle={t("auth.loginSubtitle")} footer={<>{t("auth.noAccount")} <Link to="/register" className="text-vox-bright hover:text-white" data-testid="go-register-link">{t("nav.register")}</Link></>}>
      <button type="button" onClick={startGoogleLogin} className="btn-ghost w-full" data-testid="google-login-button">
        <GoogleIcon /> {t("auth.google")}
      </button>
      <div className="my-6 flex items-center gap-4 text-xs text-vox-dim"><span className="h-px flex-1 bg-vox-line" />{t("auth.or")}<span className="h-px flex-1 bg-vox-line" /></div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="mono-label mb-2 block" htmlFor="login-email">{t("auth.email")}</label>
          <input id="login-email" type="email" required className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" autoComplete="email" />
        </div>
        <div>
          <label className="mono-label mb-2 block" htmlFor="login-password">{t("auth.password")}</label>
          <input id="login-password" type="password" required className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" autoComplete="current-password" />
        </div>
        {error && <div className="rounded-xl border border-vox-rec/30 bg-vox-rec/10 px-4 py-3 text-sm text-[#ff9b9e]" data-testid="login-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit-button">
          {busy ? <Loader2 size={16} className="animate-spin" /> : t("auth.submitLogin")}
        </button>
      </form>
    </AuthShell>
  );
}
